import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { apiResponse } from 'src/app/models/apiResponse.model';
import { ApiFilter, DefaultFilter, FieldsSelection, GroupByConfig, OrderByConfig } from 'src/app/models/apiParams.model';
import { TransferredToStockRequest } from 'src/app/models/transferredToStock.model';
import {
    addImage,
    assignExpertToValuation,
    assignValuationTasks,
    available,
    buyValuation,
    closeValuationTasks,
    contactTask,
    dateDelivery,
    extendBid,
    finishValuation,
    linkExpert,
    moveValuation,
    paymentRequest,
    preValuate,
    prepareSale,
    Reactivate,
    registerTransport,
    resellValuation,
    saleStatus,
    sellAction,
    topDownCalculation,
    transportAddress,
    updateCustomer,
    updateDamage,
    updateDamageSpecification,
    updateImage,
    updateValuation,
    updateValuationComment,
    updateVehicle,
    valuate,
    reValuate,
    valuationClaim,
    valuationComment,
    valuationDocument
} from 'src/app/models/request.model';
import { createModelMapping } from 'src/app/models/modelMapping';
import { BaseApiService } from '../base/base-api.service';

/**
 * Valuation Service - Handles valuations, tasks, documents, and third-party integrations
 */
@Injectable({ providedIn: 'root' })
export class ValuationService extends BaseApiService {
    private readonly basePath = '/request/v1/valuation/';
    private readonly taskPath = '/request/v1/valuation/task';

    constructor(http: HttpClient) {
        super(http);
    }

    /*-------------------------
    == VALUATION CALLS ==
    -------------------------*/

    getValuations(limit: number, page: number, orderBy?: string | OrderByConfig[], filter?: string | ApiFilter, fields?: FieldsSelection, defaultFilter?: DefaultFilter, groupBy?: GroupByConfig, paginator?: boolean): Observable<apiResponse> {
        const params: any = { limit, page, orderBy, filter, fields: fields ?? undefined, defaultFilter, groupBy };
        if (paginator !== undefined) params.customParams = { paginator: String(paginator) };
        return this.getAll<apiResponse>(this.basePath, params);
    }

    getValuationID(taxId: number | string, fields?: FieldsSelection): Observable<apiResponse> {
        return this.getById<apiResponse>(this.basePath, taxId, { fields });
    }

    getLogs(valuationId: number, limit: number, page: number, orderBy?: string | OrderByConfig[], filter?: string | ApiFilter, fields?: FieldsSelection, defaultFilter?: DefaultFilter, groupBy?: GroupByConfig): Observable<apiResponse> {
        return this.getAll<apiResponse>(`${this.basePath}${valuationId}/log`, { limit, page, orderBy, filter, fields, defaultFilter, groupBy });
    }

    deleteValuation(id: number): Observable<apiResponse> {
        return this.delete<apiResponse>(this.basePath, id);
    }

    /*-------------------------
    == VALUATION UPDATE CALLS ==
    -------------------------*/

    updateValuation(taxid: number, data: updateValuation): Observable<apiResponse> {
        return this.put<apiResponse>(this.basePath, taxid, data, 'json');
    }

    moveValuation(taxid: string, data: moveValuation): Observable<apiResponse> {
        return this.putPath<apiResponse>(`${this.basePath}${taxid}/move`, data, 'json');
    }

    valuateValuation(data: valuate, id: number): Observable<apiResponse> {
        return this.putPath<apiResponse>(`${this.basePath}${id}/valuate`, data, 'json');
    }

    revaluateValuation(data: reValuate, id: number): Observable<apiResponse> {
        return this.putPath<apiResponse>(`${this.basePath}${id}/revaluate`, data, 'json');
    }

    resellValuation(data: resellValuation, id: number): Observable<apiResponse> {
        return this.putPath<apiResponse>(`${this.basePath}${id}/resell`, data, 'json');
    }

    preValuateValuation(data: preValuate, id: number): Observable<apiResponse> {
        return this.putPath<apiResponse>(`${this.basePath}${id}/pre-valuate`, data, 'json');
    }

    invoiceReceived(id: number): Observable<apiResponse> {
        return this.putPath<apiResponse>(`${this.basePath}${id}/invoice-received`, {}, 'json');
    }

    updateVehicleSpecification(id: number, data: string): Observable<apiResponse> {
        const headers = new HttpHeaders().set('Accept', 'application/json').set('content-type', 'application/json');
        return this.http.put<apiResponse>(`${this.api_base}${this.basePath}${id}/vehicle-specification`, data, { headers });
    }

    updateVehicle(id: number, data: updateVehicle): Observable<apiResponse> {
        return this.putPath<apiResponse>(`${this.basePath}${id}/vehicle`, data, 'json');
    }

    updateDamageSpecification(id: number, data: updateDamageSpecification): Observable<apiResponse> {
        return this.putPath<apiResponse>(`${this.basePath}${id}/damage-specification`, data, 'json');
    }

    updateCustomer(id: number, data: updateCustomer): Observable<apiResponse> {
        return this.putPath<apiResponse>(`${this.basePath}${id}/customer/`, data, 'json');
    }

    reactivateValuation(id: number, data: Reactivate): Observable<apiResponse> {
        return this.putPath<apiResponse>(`${this.basePath}${id}/reactivate`, data, 'json');
    }

    transferredToStock(id: number, data: TransferredToStockRequest): Observable<apiResponse> {
        return this.putPath<apiResponse>(`${this.basePath}${id}/transferred-to-stock`, data, 'json');
    }

    /*-------------------------
    == VALUATION BID CALLS ==
    -------------------------*/

    extendBid(taxid: string, data: extendBid): Observable<apiResponse> {
        return this.putPath<apiResponse>(`${this.basePath}${taxid}/extend-bid`, data, 'json');
    }

    buyValuation(taxid: string, params: buyValuation): Observable<apiResponse> {
        return this.putPath<apiResponse>(`${this.basePath}${taxid}/buy`, params, 'json');
    }

    expertBid(id: number, data: any): Observable<apiResponse> {
        return this.putPath<apiResponse>(`${this.basePath}${id}/expert-bid`, data, 'json');
    }

    /*-------------------------
    == VALUATION WORKFLOW CALLS ==
    -------------------------*/

    finishValuation(id: number, data: finishValuation): Observable<apiResponse> {
        return this.putPath<apiResponse>(`${this.basePath}${id}/finish`, data, 'json');
    }

    changeDateDelivery(id: number, data: dateDelivery): Observable<apiResponse> {
        return this.putPath<apiResponse>(`${this.basePath}${id}/date-delivery`, data, 'json');
    }

    valuationAvailable(id: number, data: available): Observable<apiResponse> {
        return this.putPath<apiResponse>(`${this.basePath}${id}/available`, data, 'json');
    }

    deleteAvailableStatus(id: number): Observable<apiResponse> {
        return this.delete<apiResponse>(`${this.basePath}`, `${id}/available/`);
    }

    createContactTask(id: number, data: contactTask): Observable<apiResponse> {
        return this.putPath<apiResponse>(`${this.basePath}${id}/contact-task`, data, 'json');
    }

    /*-------------------------
    == VALUATION TRANSPORT CALLS ==
    -------------------------*/

    transportAddress(id: number, data: transportAddress): Observable<apiResponse> {
        return this.putPath<apiResponse>(`${this.basePath}${id}/transport-address`, data, 'json');
    }

    registerTransport(id: number, data: registerTransport): Observable<apiResponse> {
        return this.putPath<apiResponse>(`${this.basePath}${id}/transport`, data, 'json');
    }

    transported(id: number): Observable<apiResponse> {
        return this.putPath<apiResponse>(`${this.basePath}${id}/transported`, {}, 'json');
    }

    /*-------------------------
    == VALUATION SALE CALLS ==
    -------------------------*/

    sellValuation(id: number, data: sellAction): Observable<apiResponse> {
        return this.putPath<apiResponse>(`${this.basePath}${id}/sell`, data, 'json');
    }

    prepareSale(id: number, data: prepareSale): Observable<apiResponse> {
        return this.putPath<apiResponse>(`${this.basePath}${id}/prepare-sale`, data, 'json');
    }

    saleStatus(id: number, data: saleStatus): Observable<apiResponse> {
        return this.putPath<apiResponse>(`${this.basePath}${id}/sale-status/`, data, 'json');
    }

    deleteSaleStatus(id: number): Observable<apiResponse> {
        return this.delete<apiResponse>(`${this.basePath}`, `${id}/sale-status/`);
    }

    mailPaymentRequest(id: number, data: paymentRequest): Observable<apiResponse> {
        return this.putPath<apiResponse>(`${this.basePath}${id}/payment-request/`, data, 'json');
    }

    /*-------------------------
    == VALUATION DOCUMENT CALLS ==
    -------------------------*/

    getValuationDocument(documentId: number): Observable<any> {
        const headers = new HttpHeaders().set('Accept', 'application/json');
        return this.http.get(`${this.api_base}/request/v1/valuation/document/${documentId}/content`, { headers, responseType: 'blob' });
    }

    addValuationDocument(id: number, params: valuationDocument): Observable<apiResponse> {
        return this.post<apiResponse>(`${this.basePath}${id}/document`, params, 'json');
    }

    createValuationDocument(id: number, params: valuationDocument): Observable<apiResponse> {
        return this.post<apiResponse>(`${this.basePath}${id}/document`, params, 'json');
    }

    updateValuationDocument(id: number, documentId: number, data: any): Observable<apiResponse> {
        return this.putPath<apiResponse>(`${this.basePath}${id}/document/${documentId}`, data, 'json');
    }

    deleteValuationDocument(id: number, documentId: number): Observable<apiResponse> {
        return this.delete<apiResponse>(`${this.basePath}`, `${id}/document/${documentId}`);
    }

    /*-------------------------
    == VALUATION COMMENT CALLS ==
    -------------------------*/

    getValuationCommentTypes(limit: number, page: number, orderBy: string, filter: string | ApiFilter, fields: FieldsSelection, defaultFilter: DefaultFilter): Observable<apiResponse> {
        return this.getAll<apiResponse>('/request/v1/valuation/comment-type', { limit, page, orderBy, filter, fields, defaultFilter });
    }

    createValuationComment(id: number, params: valuationComment): Observable<apiResponse> {
        return this.post<apiResponse>(`${this.basePath}${id}/comment`, params, 'json');
    }

    updateValuationComment(taxid: number, data: updateValuationComment, commentid: number): Observable<apiResponse> {
        return this.putPath<apiResponse>(`${this.basePath}${taxid}/comment/${commentid}`, data, 'json');
    }

    deleteComment(id: number): Observable<apiResponse> {
        return this.delete<apiResponse>('/request/v1/valuation/comment/', id);
    }

    /*-------------------------
    == VALUATION CLAIM CALLS ==
    -------------------------*/

    createValuationClaim(id: number, params: valuationClaim): Observable<apiResponse> {
        return this.post<apiResponse>(`${this.basePath}${id}/claim`, params, 'json');
    }

    /*-------------------------
    == VALUATION TASK CALLS ==
    -------------------------*/

    getTasks(limit: number, page: number, orderBy?: string | OrderByConfig[], filter?: string | ApiFilter, fields?: FieldsSelection, defaultFilter?: DefaultFilter, groupBy?: GroupByConfig): Observable<apiResponse> {
        return this.getAll<apiResponse>(this.taskPath, { limit, page, orderBy, filter, fields, defaultFilter, groupBy });
    }

    getTaskTypes(limit: number, page: number, orderBy?: string, filter?: string | ApiFilter, fields?: FieldsSelection, defaultFilter?: DefaultFilter): Observable<apiResponse> {
        return this.getAll<apiResponse>(`${this.taskPath}-type`, { limit, page, orderBy, filter, fields, defaultFilter });
    }

    assignValuationTasks(data: assignValuationTasks): Observable<apiResponse> {
        const headers = new HttpHeaders().set('Accept', 'application/json');
        return this.http.put<apiResponse>(`${this.api_base}${this.taskPath}/assign`, JSON.stringify(data), { headers });
    }

    closeValuationTask(id: number): Observable<apiResponse> {
        return this.putPath<apiResponse>(`${this.taskPath}/${id}/close`, {}, 'json');
    }

    closeValuationTasks(data: closeValuationTasks): Observable<apiResponse> {
        return this.putPath<apiResponse>(`${this.taskPath}/close`, data, 'json');
    }

    getTaskOptions(): Observable<apiResponse> {
        return this.options<apiResponse>(this.taskPath);
    }

    /*-------------------------
    == VALUATION TYPE CALLS ==
    -------------------------*/

    getValuationStatusTypes(limit: number, page: number, orderBy?: string, filter?: string | ApiFilter, fields?: FieldsSelection, defaultFilter?: DefaultFilter): Observable<apiResponse> {
        return this.getAll<apiResponse>(`${this.basePath}status-type`, { limit, page, orderBy, filter, fields, defaultFilter });
    }

    getValuationResultTypes(limit: number, page: number, orderBy: string, filter: string | ApiFilter, fields: FieldsSelection, defaultFilter: DefaultFilter): Observable<apiResponse> {
        return this.getAll<apiResponse>(`${this.basePath}result-type`, { limit, page, orderBy, filter, fields, defaultFilter });
    }

    getValuationInspectionAddress(valuationId: number): Observable<apiResponse> {
        return this.getAll<apiResponse>(`${this.basePath}${valuationId}/inspection-address`);
    }

    /*-------------------------
    == VALUATION EXPERT CALLS ==
    -------------------------*/

    assignExpertToValuation(id: number, data: assignExpertToValuation): Observable<apiResponse> {
        return this.putPath<apiResponse>(`${this.basePath}${id}/assign-expert`, data, 'json');
    }

    linkExpert(id: number, data: linkExpert): Observable<apiResponse> {
        return this.putPath<apiResponse>(`${this.basePath}${id}/link-expert`, data, 'json');
    }

    /*-------------------------
    == VALUATION RECOMMENDATION CALLS ==
    -------------------------*/

    private readonly recommendationPath = '/request/v1/valuation/recommendation';

    getRecommendations(limit: number, page: number, orderBy?: string | OrderByConfig[], filter?: string | ApiFilter, fields?: FieldsSelection, defaultFilter?: DefaultFilter): Observable<apiResponse> {
        return this.getAll<apiResponse>(this.recommendationPath, { limit, page, orderBy, filter, fields, defaultFilter });
    }

    createRecommendation(recommendations: { valuationId: number; companyIds: number[] }[]): Observable<apiResponse> {
        return this.post<apiResponse>(this.recommendationPath, { recommendations }, 'json');
    }

    /*-------------------------
    == VALUATION INSPECTION CALLS ==
    -------------------------*/

    approveInspection(id: number, data: any): Observable<apiResponse> {
        return this.putPath<apiResponse>(`${this.basePath}${id}/inspection-approve`, data, 'json');
    }

    rejectInspection(id: number, data: any): Observable<apiResponse> {
        return this.putPath<apiResponse>(`${this.basePath}${id}/inspection-reject`, data, 'json');
    }

    inspection(id: number): Observable<apiResponse> {
        return this.putPath<apiResponse>(`${this.basePath}${id}/inspection-request`, {}, 'json');
    }

    cancelInspection(id: number): Observable<apiResponse> {
        return this.delete<apiResponse>(`${this.basePath}`, `${id}/inspection-request`);
    }

    planInspection(id: number, data: any): Observable<apiResponse> {
        return this.putPath<apiResponse>(`${this.basePath}${id}/inspection`, data, 'json');
    }

    inspectionAddress(id: number, data: any): Observable<apiResponse> {
        return this.putPath<apiResponse>(`${this.basePath}${id}/inspection-address`, data, 'json');
    }

    /*-------------------------
    == REFERENCE BID CALLS ==
    -------------------------*/

    getReferenceBids(limit: number, page: number, fields?: string | null, defaultFilter?: string | null, filter?: string | null, groupBy?: string | null, orderBy?: string | null): Observable<apiResponse> {
        return this.getAll<apiResponse>(`${this.basePath}reference-bid`, { limit, page, orderBy: orderBy || undefined, filter: filter || undefined, fields: fields || undefined, defaultFilter: defaultFilter || undefined, groupBy: groupBy || undefined });
    }

    getReferenceBidOptions(): Observable<apiResponse> {
        return this.options<apiResponse>(`${this.basePath}reference-bid`);
    }

    /*-------------------------
    == TOP DOWN CALCULATION CALLS ==
    -------------------------*/

    topDownCalculation(id: number, params: topDownCalculation): Observable<apiResponse> {
        return this.post<apiResponse>(`${this.basePath}${id}/top-down-calculation`, params, 'json');
    }

    /*-------------------------
    == THIRD PARTY LINKS ==
    -------------------------*/

    getAutotrackLinkValuation(id: number): Observable<apiResponse> {
        return this.getAll<apiResponse>(`${this.basePath}${id}/autotrack`);
    }

    getGaspedaalLinkValuation(id: number): Observable<apiResponse> {
        return this.getAll<apiResponse>(`${this.basePath}${id}/gaspedaal`);
    }

    getAutowereldLinkValuation(id: number): Observable<apiResponse> {
        return this.getAll<apiResponse>(`${this.basePath}${id}/autowereld`);
    }

    getMobileDeLinkValuation(id: number): Observable<apiResponse> {
        return this.getAll<apiResponse>(`${this.basePath}${id}/mobile-de`);
    }

    getSourceData(id: number): Observable<apiResponse> {
        return this.getAll<apiResponse>(`${this.basePath}${id}/source-data`);
    }

    /*-------------------------
    == MAPPING CALLS ==
    -------------------------*/

    getMappingModels(limit: number, page: number, fields?: string | null, defaultFilter?: string | null, filter?: string | null, groupBy?: string | null, orderBy?: string | null): Observable<apiResponse> {
        return this.getAll<apiResponse>('/request/v1/mapping/model/', { limit, page, orderBy: orderBy || undefined, filter: filter || undefined, fields: fields || undefined, defaultFilter: defaultFilter || undefined, groupBy: groupBy || undefined });
    }

    getMappingModel(id: number, fields?: string): Observable<apiResponse> {
        return this.getById<apiResponse>('/request/v1/mapping/model/', id, fields ? { customParams: { fields } } : undefined);
    }

    createMappingForModelSpecific(params: createModelMapping): Observable<apiResponse> {
        return this.post<apiResponse>('/request/v1/model-specific', params, 'json');
    }

    createMappingForModel(id: number, params: any): Observable<apiResponse> {
        return this.post<apiResponse>(`/request/v1/model/${id}`, params, 'json');
    }

    /*-------------------------
    == SPECIFICATION CALLS ==
    -------------------------*/

    deleteSpecification(id: number): Observable<apiResponse> {
        return this.delete<apiResponse>('/request/v1/specification/', id);
    }

    /*-------------------------
    == VEHICLE ACCESSORY CALLS ==
    -------------------------*/

    deleteVehicleAccessory(id: number, accessoryId: number): Observable<apiResponse> {
        return this.delete<apiResponse>(`${this.basePath}${id}/vehicle-accessory/`, accessoryId);
    }

    /*-------------------------
    == IMAGE CALLS ==
    -------------------------*/

    addImage(data: addImage, descriptionId: number): Observable<apiResponse> {
        const headers = new HttpHeaders().set('Accept', 'application/json');
        return this.http.post<apiResponse>(`${this.api_base}${this.basePath}${descriptionId}/image/`, data, { headers });
    }

    updateImage(descriptionId: number, imageId: number, params: updateImage): Observable<apiResponse> {
        const headers = new HttpHeaders().set('Accept', 'application/json');
        return this.http.put<apiResponse>(`${this.api_base}${this.basePath}${descriptionId}/image/${imageId}`, params, { headers });
    }

    deleteImage(descriptionId: number, imageId: number): Observable<apiResponse> {
        const headers = new HttpHeaders().set('Accept', 'application/json');
        return this.http.delete<apiResponse>(`${this.api_base}${this.basePath}${descriptionId}/image/${imageId}`, { headers });
    }

    updateDamge(damageId: number, valuationId: number, params: updateDamage): Observable<apiResponse> {
        const headers = new HttpHeaders().set('Accept', 'application/json');
        return this.http.put<apiResponse>(`${this.api_base}${this.basePath}${valuationId}/damage-specification/${damageId}`, params, { headers });
    }
}
